import { ReactNode } from 'react'

import * as Sentry from '@sentry/react'
import { SWRConfig } from 'swr'

import { makeParamsQueryStr, replacePath } from '@/common/api/rest'
import { notifyUnauthorized } from '@/common/api/session'
import { HttpStatusCode } from '@/common/util/status'
import { LocalStorage, loadStorage } from '@/common/util/storage'

const NO_LOGGING_STATUS: number[] = [
  HttpStatusCode.UNAUTHORIZED,
  HttpStatusCode.NOT_FOUND,
  HttpStatusCode.VALIDATION_FAILED
]

export interface Params {
  [index: string]: any
}

/**
 * 認証ヘッダを組み立てる。`Authorization` は常にここで上書きする。
 *
 * `FormData` のときは `Content-Type` を落とす。境界（boundary）付きの multipart は
 * ブラウザに決めさせる必要があり、`application/json` のまま送るとサーバがボディを
 * 解釈できず 422 になる。
 *
 * 逆に、文字列のボディ（`JSON.stringify` したもの）で `Content-Type` が無いときは
 * `application/json` を付ける。付けないとブラウザが `text/plain` で送り、FastAPI は
 * ボディを「JSON の文字列そのもの」として受け取って 422 になる（VOC の LP コピー生成で発生）。
 */
const authorizedHeaders = (init?: RequestInit): Headers => {
  const headers = new Headers(init?.headers)
  // TODO: change to cookie for http-only security
  headers.set('Authorization', `Bearer ${loadStorage(LocalStorage.AUTH_TOKEN) || ''}`)
  if (init?.body instanceof FormData) headers.delete('Content-Type')
  else if (typeof init?.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  return headers
}

/**
 * 認証ヘッダを付けて叩き、JSON を返す API 呼び出しの中心。
 *
 * SWR を経由しない直接の呼び出しも**素の `fetch` ではなくこれを使う**こと。
 * トークンの出どころ（localStorage）と 401 の検知が 1 箇所に集まる。
 * 失敗時は `Response` を throw するので、呼び出し側は `catch` で status を見る。
 *
 * レスポンスが JSON でないもの（ファイルダウンロード・SSE）だけは、
 * `apiGetBlobFetcher` や各ストリーム側で個別に扱う。
 */
export const fetchRest = async <T,>(
  input: string | URL | Request,
  init?: RequestInit
): Promise<T> => {
  return fetch(input, { ...init, headers: authorizedHeaders(init) }).then(async (response) => {
    if (!response.ok) {
      // セッション切れの後始末は AuthProvider が握っている（session.ts 経由で通知）
      if (response.status === HttpStatusCode.UNAUTHORIZED) {
        notifyUnauthorized()
      }
      if (response instanceof Response && !NO_LOGGING_STATUS.includes(response.status)) {
        Sentry.captureException(response)
      }
      throw response
    }

    return response.json()
  })
}

export const apiDefaultFetcher = async <T,>(
  input: string | [string, Params],
  params?: { arg: Params }
): Promise<T> => {
  const isArray = Array.isArray(input)
  const path = isArray ? input[0] : input
  const args: Params = (isArray ? input[1] : params ? params.arg : {}) as Params
  const { path: filteredPath, params: filteredArg } = replacePath(path, args)

  return fetchRest<T>(filteredPath + makeParamsQueryStr(filteredArg), {
    method: 'GET'
  })
}

export const apiPostFetcher = async <T,>(
  input: string | [string, Params],
  params?: { arg: Params },
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST'
): Promise<T> => {
  const isArray = Array.isArray(input)
  const path = isArray ? input[0] : input
  const args: Params = (isArray ? input[1] : params ? params.arg : {}) as Params

  const { path: filteredPath, params: filteredArg } = replacePath(path, args)

  return fetchRest<T>(filteredPath, {
    body: JSON.stringify(filteredArg),
    method,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

export const apiGetBlobFetcher = async (
  input: string | [string, Params],
  params?: { arg: Params }
): Promise<Blob> => {
  const isArray = Array.isArray(input)
  const path = isArray ? input[0] : input
  const args: Params = (isArray ? input[1] : params ? params.arg : {}) as Params
  const { path: filteredPath, params: filteredArg } = replacePath(path, args)

  const init: RequestInit = { method: 'GET' }
  const response = await fetch(filteredPath + makeParamsQueryStr(filteredArg), {
    ...init,
    headers: authorizedHeaders(init)
  })
  if (!response.ok) {
    if (response.status === HttpStatusCode.UNAUTHORIZED) notifyUnauthorized()
    throw response
  }
  return response.blob()
}

export const apiPutFetcher = async <T,>(
  input: string | [string, Params],
  params?: { arg: Params }
): Promise<T> => {
  return apiPostFetcher(input, params, 'PUT')
}

/** 部分更新。送った項目だけを変える API に使う（PUT は全置換の意味になる） */
export const apiPatchFetcher = async <T,>(
  input: string | [string, Params],
  params?: { arg: Params }
): Promise<T> => {
  return apiPostFetcher(input, params, 'PATCH')
}

export const apiDeleteFetcher = async <T,>(
  input: string | [string, Params],
  params?: { arg: Params }
): Promise<T> => {
  return apiPostFetcher(input, params, 'DELETE')
}

export const DefaultSWRConfig = ({
  children,
  fallback
}: {
  children: ReactNode
  fallback: any
}) => {
  return (
    <SWRConfig
      value={{
        fetcher: apiDefaultFetcher,
        fallback,
        revalidateOnMount: true,
        revalidateIfStale: true,
        revalidateOnFocus: false,
        // by fetchRest function
        // onError: (error, key) => {
        // if (![HttpStatusCode.UNAUTHORIZED, HttpStatusCode.NOT_FOUND].includes(error.status)) {
        // Sentry.captureException(error)
        // }
        // },
        onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
          if (
            [
              HttpStatusCode.UNAUTHORIZED,
              HttpStatusCode.NOT_FOUND,
              HttpStatusCode.VALIDATION_FAILED
            ].includes(error.status)
          ) {
            return
          }
          if (retryCount >= 3) {
            return
          }
          setTimeout(() => revalidate({ retryCount }), 5000)
        }
      }}
    >
      {children}
    </SWRConfig>
  )
}
